/**
 * DowngradeBanner Component
 *
 * Shows a warning banner when a downgrade is scheduled,
 * with the option to cancel it.
 */

import { AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/v2/ui/Button'

export function DowngradeBanner({
  toTier,
  toPrice,
  date,
  onCancel,
  loading,
}: {
  toTier: string
  toPrice?: number
  date: string
  onCancel: () => void
  loading: boolean
}) {
  const { t } = useTranslation()
  const tierLabel = toTier.charAt(0).toUpperCase() + toTier.slice(1)
  return (
    <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
            {t('billing.downgradeScheduled', { tier: tierLabel, date })}
          </p>
          {toPrice != null && (
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5 font-medium">
              {t('billing.downgradeNewPrice', { price: toPrice.toFixed(2).replace('.', ','), tier: tierLabel })}
            </p>
          )}
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
            {t('billing.downgradeCreditsWarning')}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={onCancel}
            disabled={loading}
          >
            {loading ? t('billing.cancelingDowngrade') : t('billing.cancelDowngrade')}
          </Button>
        </div>
      </div>
    </div>
  )
}
